import { Injectable, inject } from '@angular/core';
import { combineLatest, filter, map, Observable, of, shareReplay, switchMap } from 'rxjs';

import { GlobalConfigurationsService } from './global-configuration.service';
import { PermissionsService } from './permissions.service';
import { CurrentConfigurationDto } from '@api/backend-connector/models';
import { CurrentConfigurationApi } from '@api/backend-connector/services';

const ProjectDashboardProperty = 'projectDashboard';
type ProjectDashboardConfiguration = {
  charts?: {
    dynamicGrouping?: string;
    managementIntervention?: boolean;
  };
};

// modules enabled by default (must be turned off explicitely)
const EconomicsDisabledProperty = 'economicsDisabled';
const InventoryDisabledProperty = 'inventoryDisabled';
const FilesDisabledProperty = 'filesDisabled';
const GlobalConfigurationDisabledProperty = 'globalConfigurationDisabled';

// modules disabled by default (must be turned on explicitely)
const AssetFilterEnabledProperty = 'assetFilterEnabled'; // this also requires a dynamic field to work
const GuastoFilterEnabledProperty = 'guastoFilterEnabled'; // this also requires a dynamic field to work
export const KnownCustomDynamicFields = {
  guasto: 'guasto',
  asset: 'asset',
  cadenzaScheda: 'cadenzaScheda',
};

export const translateCustomField = (customField?: string) => {
  switch (customField) {
    case 'guasto':
      return `<span>il</span> <b>${customField}</b>`;
    case 'cadenzaScheda':
      return '<span>la</span> <b>cadenza</b> <span>della scheda</span>';
    case 'asset':
      return '<span>l\'</span><b>asset</b> <span>della scheda</span>';

    default:
      break;
  }
  return '<span>i</span> <b>campi</b>';
};

@Injectable({
  providedIn: 'root',
})
export class CurrentConfigurationsService {
  private readonly currentConfigurationApi = inject(CurrentConfigurationApi);
  private readonly globalConfigurationsService = inject(GlobalConfigurationsService);
  private readonly permissionsService = inject(PermissionsService);

  private _currentConfiguration$ = combineLatest([
    this.permissionsService.isSuperAdmin(),
    this.globalConfigurationsService.refresh$,
  ]).pipe(
    switchMap(([isSuperAdmin]) => {
      if (isSuperAdmin) {
        return of(undefined);
      }
      return this.currentConfigurationApi.getCurrentConfiguration();
    }),
    shareReplay({ refCount: false, bufferSize: 1 }),
  );

  getCurrentConfigurationRaw(): Observable<CurrentConfigurationDto | undefined> {
    return this._currentConfiguration$;
  }

  getCurrentConfiguration(): Observable<CurrentConfigurationDto> {
    return this._currentConfiguration$.pipe(filter((c): c is CurrentConfigurationDto => !!c));
  }

  /**
   * Used in project creation. Budget can be enabled in other projects
   * @returns
   */
  budgetModuleEnabled(): Observable<boolean> {
    return this._currentConfiguration$.pipe(map((c) => !!c?.budgetModuleEnabled));
  }

  /**
   * Used to disable the budget module platform wide.
   * @returns
   */
  economicsEnabled(): Observable<boolean> {
    return this._currentConfiguration$.pipe(map((c) => !!c && !c.data[EconomicsDisabledProperty]));
  }

  inventoryEnabled(): Observable<boolean> {
    return this._currentConfiguration$.pipe(map((c) => !!c && !c.data[InventoryDisabledProperty]));
  }

  filesEnabled(): Observable<boolean> {
    return this._currentConfiguration$.pipe(map((c) => !!c && !c.data[FilesDisabledProperty]));
  }

  globalConfigurationEnabled(): Observable<boolean> {
    return this._currentConfiguration$.pipe(map((c) => !!c && !c.data[GlobalConfigurationDisabledProperty]));
  }

  projectDashboardConfiguration(): Observable<ProjectDashboardConfiguration> {
    return this._currentConfiguration$.pipe(map((c) => (c && c.data[ProjectDashboardProperty]) ?? {}));
  }

  showCustomAssetFilter() {
    return this._currentConfiguration$.pipe(map((c) => c && !!c.data[AssetFilterEnabledProperty]));
  }

  showCustomGuastoFilter() {
    return this._currentConfiguration$.pipe(map((c) => c && !!c.data[GuastoFilterEnabledProperty]));
  }

  projectStatuses() {
    return this._currentConfiguration$.pipe(map((c) => (c ? c.projectStatuses : undefined)));
  }

  pushNotificationsPublicKey() {
    return this._currentConfiguration$.pipe(map((c) => (c ? c.pushPublicKey : undefined)));
  }
}
